/** Pedidos: lista por estado y ficha con historial, pago, envío e incidencia (1.0 AdminOrders). */
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { ORDER_STATUSES } from '@nuvvu/shared';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, muted, Notice, Table, td, when, money, str } from '../lib/ui.js';

const CANCELLABLE = [
  'paid_pending_acceptance',
  'accepted',
  'label_ready',
  'shipped',
  'in_transit',
  'delivered',
  'disputed',
];
const RELEASABLE = ['accepted', 'label_ready', 'shipped', 'in_transit', 'delivered'];

/** Salida para un pedido atascado: cancelar y reembolsar, o liberar el pago (1.0 AdminOrders). */
function OrderActions({ id, status }: { id: string; status: string }): ReactElement | null {
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canCancel = CANCELLABLE.includes(status);
  const canRelease = RELEASABLE.includes(status);
  if (!canCancel && !canRelease) return null;
  const run = (action: 'cancel_refund' | 'release'): void => {
    setErr(null);
    setMsg(null);
    if (reason.trim().length < 3) {
      setErr('Escribe el motivo (queda en el historial y en la auditoría).');
      return;
    }
    const q =
      action === 'cancel_refund'
        ? '¿Cancelar el pedido y devolver TODO al comprador? El artículo vuelve a estar disponible.'
        : '¿Liberar el pago al vendedor y dar el pedido por completado?';
    if (!window.confirm(q)) return;
    setBusy(true);
    call(api.admin.orderAction, { order_id: id, action, reason: reason.trim() })
      .then(() => {
        setMsg('Hecho. Recarga la página para ver el nuevo estado.');
      })
      .catch((e: unknown) => {
        setErr(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <section style={{ ...card, display: 'grid', gap: 8 }}>
      <strong>Acciones</strong>
      <input
        style={field}
        placeholder="Motivo (obligatorio)"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
      />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {canCancel && (
          <button
            type="button"
            style={btn(true)}
            disabled={busy}
            onClick={() => {
              run('cancel_refund');
            }}
          >
            Cancelar y reembolsar al comprador
          </button>
        )}
        {canRelease && (
          <button
            type="button"
            style={btn(false)}
            disabled={busy}
            onClick={() => {
              run('release');
            }}
          >
            Liberar pago al vendedor
          </button>
        )}
      </div>
      <Notice text={msg} />
      <Notice text={err} error />
    </section>
  );
}

type Doc = Record<string, unknown>;

const SHIPPING = ['accepted', 'label_ready', 'shipped', 'in_transit'];
const MONEY = [
  'capture',
  'release_authorization',
  'refund_full',
  'refund_partial',
  'transfer_to_seller',
];

export function OrdersPage(): ReactElement {
  // Desde el Panel (27/10): `?status=…`, `?filtro=envio` (en envío) o `?filtro=dinero` (cobros que se reintentan).
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const filtro = params.get('filtro') ?? '';
  const setStatus = (s: string): void => {
    setParams(s ? { status: s } : {}, { replace: true });
  };
  const [rows, setRows] = useState<Doc[] | null>(null);
  useEffect(() => {
    setRows(null);
    const base = collection(db, 'orders');
    const q =
      filtro === 'envio'
        ? query(base, where('status', 'in', SHIPPING), limit(200))
        : filtro === 'dinero'
          ? query(base, where('money_pending.action', 'in', MONEY), limit(200))
          : status
            ? query(base, where('status', '==', status), orderBy('created_at', 'desc'), limit(100))
            : query(base, orderBy('created_at', 'desc'), limit(50));
    getDocs(q)
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {
        setRows([]);
      });
  }, [status, filtro]);
  return (
    <>
      <h1 style={{ margin: 0 }}>Pedidos</h1>
      {filtro && (
        <p style={muted}>
          {filtro === 'envio'
            ? 'En envío (aceptados, etiqueta lista, enviados, en camino).'
            : 'Con un cobro, anulación, reembolso o abono que falló y el barrido está reintentando.'}{' '}
          <Link to="/pedidos">Quitar filtro</Link>
        </p>
      )}
      <select
        style={{ ...field, width: 'auto' }}
        value={status}
        onChange={(e) => {
          setStatus(e.target.value);
        }}
      >
        <option value="">{filtro ? 'Filtro del Panel' : 'Todos (últimos 50)'}</option>
        {ORDER_STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table
          head={['Pedido', 'Estado', 'Modo', 'Artículo', 'Total', 'Comprador', 'Vendedor', 'Fecha']}
        >
          {rows.map((o) => {
            const item = ((o.items as Doc[] | undefined) ?? [])[0];
            return (
              <tr key={String(o.id)}>
                <td style={td}>
                  <Link to={`/pedidos/${String(o.id)}`}>
                    {String(o.id).slice(-6).toUpperCase()}
                  </Link>
                </td>
                <td style={td}>{String(o.status)}</td>
                <td style={td}>{String(o.fulfillment_mode)}</td>
                <td style={td}>{item ? String(item.title) : '—'}</td>
                <td style={td}>{money((o.amounts as Doc | undefined)?.total)}</td>
                <td style={td}>
                  <Link to={`/usuarios/${String(o.buyer_id)}`}>{String(o.buyer_id).slice(-6)}</Link>
                </td>
                <td style={td}>
                  <Link to={`/usuarios/${String(o.seller_id)}`}>
                    {String(o.seller_id).slice(-6)}
                  </Link>
                </td>
                <td style={td}>{when(o.created_at)}</td>
              </tr>
            );
          })}
        </Table>
      )}
    </>
  );
}

function Pre({ value }: { value: unknown }): ReactElement {
  return (
    <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {JSON.stringify(
        value,
        (_k, v: unknown) =>
          v && typeof v === 'object' && 'toDate' in v
            ? (v as { toDate: () => Date }).toDate().toISOString()
            : v,
        2,
      )}
    </pre>
  );
}

export function OrderPage(): ReactElement {
  const { id = '' } = useParams();
  const [o, setO] = useState<Doc | null | undefined>(undefined);
  const [payment, setPayment] = useState<Doc | null>(null);
  const [shipment, setShipment] = useState<Doc | null>(null);
  const [dispute, setDispute] = useState<Doc | null>(null);
  useEffect(() => {
    getDoc(doc(db, 'orders', id))
      .then(async (s) => {
        if (!s.exists()) {
          setO(null);
          return;
        }
        const data: Doc = s.data();
        setO({ id: s.id, ...data });
        const pid: unknown = data.payment_id;
        const sid: unknown = data.shipment_id;
        const did: unknown = data.dispute_id;
        if (typeof pid === 'string')
          setPayment(((await getDoc(doc(db, 'payments', pid))).data() as Doc | undefined) ?? null);
        if (typeof sid === 'string')
          setShipment(
            ((await getDoc(doc(db, 'shipments', sid))).data() as Doc | undefined) ?? null,
          );
        if (typeof did === 'string')
          setDispute(((await getDoc(doc(db, 'disputes', did))).data() as Doc | undefined) ?? null);
      })
      .catch(() => {
        setO(null);
      });
  }, [id]);
  if (o === undefined) return <p>Cargando…</p>;
  if (o === null) return <p>No existe el pedido {id}.</p>;
  const history = (o.history as Doc[] | undefined) ?? [];
  return (
    <>
      <h1 style={{ margin: 0 }}>
        Pedido #{id.slice(-6).toUpperCase()} <span style={muted}>{String(o.status)}</span>
      </h1>
      <OrderActions id={id} status={String(o.status)} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 12,
        }}
      >
        <section style={card}>
          <strong>Resumen</strong>
          <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
            <span>
              Modo: {String(o.fulfillment_mode)} · Moneda:{' '}
              {str((o.amounts as Doc | undefined)?.currency)}
            </span>
            <span>
              Total: {money((o.amounts as Doc | undefined)?.total)} · Artículo:{' '}
              {money((o.amounts as Doc | undefined)?.item)} · Envío:{' '}
              {money((o.amounts as Doc | undefined)?.shipping)} · Protección:{' '}
              {money((o.amounts as Doc | undefined)?.protection)}
            </span>
            <span>
              Comprador: <Link to={`/usuarios/${String(o.buyer_id)}`}>{String(o.buyer_id)}</Link>
            </span>
            <span>
              Vendedor: <Link to={`/usuarios/${String(o.seller_id)}`}>{String(o.seller_id)}</Link>
            </span>
            <span>
              Creado: {when(o.created_at)} · Actualizado: {when(o.updated_at)}
            </span>
            <span>
              Plazos: aceptar {when((o.deadlines as Doc | undefined)?.accept_by) || '—'} · enviar{' '}
              {when((o.deadlines as Doc | undefined)?.ship_by) || '—'} · reclamar{' '}
              {when((o.deadlines as Doc | undefined)?.dispute_by) || '—'} · liberar{' '}
              {when((o.deadlines as Doc | undefined)?.release_at) || '—'}
            </span>
          </div>
        </section>
        <section style={card}>
          <strong>Historial</strong>
          <Table head={['Cuándo', 'Evento', 'De → a', 'Actor']}>
            {history.map((h, i) => (
              <tr key={i}>
                <td style={td}>{when(h.at)}</td>
                <td style={td}>{String(h.event)}</td>
                <td style={td}>
                  {String(h.from)} → {String(h.to)}
                </td>
                <td style={td}>{JSON.stringify(h.actor)}</td>
              </tr>
            ))}
          </Table>
        </section>
        {payment && (
          <section style={card}>
            <strong>Pago</strong>
            <Pre value={payment} />
          </section>
        )}
        {shipment && (
          <section style={card}>
            <strong>Envío</strong>
            <Pre value={shipment} />
          </section>
        )}
        {dispute && (
          <section style={card}>
            <strong>Incidencia</strong>
            <Link to="/incidencias">Ver cola de incidencias →</Link>
            <Pre value={dispute} />
          </section>
        )}
        <section style={card}>
          <strong>Documento</strong>
          <Pre value={o} />
        </section>
      </div>
    </>
  );
}
